import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch, getAccessToken } from './client';
import type { IrsForm, IrsFormData } from '@spi/shared';

export function useIrsForms() {
  return useQuery({
    queryKey: ['irs-forms'],
    queryFn: () => apiFetch<IrsForm[]>('/api/irs'),
  });
}

export function usePrefillIrsForm(donationId: number, doneeMode: 'spi' | 'recipient', enabled = true) {
  return useQuery({
    queryKey: ['irs-prefill', donationId, doneeMode],
    queryFn: () => apiFetch<IrsFormData>(`/api/irs/${donationId}/prefill?doneeMode=${doneeMode}`),
    enabled,
  });
}

export function useGenerateIrsForm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ donationId, formData, signatureDataUrl }: { donationId: number; formData: IrsFormData; signatureDataUrl?: string }) =>
      apiFetch<IrsForm>(`/api/irs/${donationId}/generate`, {
        method: 'POST',
        body: JSON.stringify({ formData, signatureDataUrl }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['irs-forms'] });
      qc.invalidateQueries({ queryKey: ['donations'] });
    },
  });
}

export function useEmailIrsForm() {
  return useMutation({
    mutationFn: (donationId: number) =>
      apiFetch<{ message: string }>(`/api/irs/${donationId}/email`, { method: 'POST' }),
  });
}

export async function downloadIrsForm(donationId: number): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`/api/irs/${donationId}/download`, {
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Download failed' }));
    throw new Error(body.message);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `irs-8283-transaction-${donationId}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function previewIrsForm(donationId: number): Promise<string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`/api/irs/${donationId}/download`, {
    headers,
    credentials: 'include',
    cache: 'no-store',
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: 'Preview failed' }));
    throw new Error(body.message);
  }

  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// ============================================================
// Admin Saved Signatures
// ============================================================

export interface SavedSignature {
  id: number;
  userId: number;
  label: string;
  signatureData: string;
  createdAt: string;
}

export function useSavedSignatures(enabled = true) {
  return useQuery({
    queryKey: ['admin-signatures'],
    queryFn: () => apiFetch<SavedSignature[]>('/api/irs/signatures'),
    enabled,
  });
}

export function useSaveSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ label, signatureData }: { label: string; signatureData: string }) =>
      apiFetch<SavedSignature>('/api/irs/signatures', {
        method: 'POST',
        body: JSON.stringify({ label, signatureData }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-signatures'] });
    },
  });
}

export function useDeleteSignature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiFetch<void>(`/api/irs/signatures/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-signatures'] });
    },
  });
}
